import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import background from "../image/backgrounds/guide_frame.png";
import Grid from "./Grid";
import {
  selectBountyPool,
  selectInterest,
  selectNonce,
  selectRedeemCostBase,
  selectRedeemInfo,
  selectRedeemRewardBase,
  setUIState,
  UIState,
  UIStateType,
} from "../../data/properties";
import "./RedeemScene.css";
import {
  getResourceDisabledIconPath,
  getResourceIconPath,
  ResourceType,
  resourceTypes,
} from "../../data/models";
import RedeemDisplay from "./RedeemDisplay";
import RedeemDisplayCollectInterest from "./RedeemDisplayCollectInterest";
import { getRedeemTransactionCommandArray } from "../rpc";
import {
  useWalletContext,
  queryState,
  sendTransaction,
} from "zkwasm-minirollup-browser";

import { selectResources } from "../../data/resources";
import {
  LoadingType,
  pushError,
  selectIsLoading,
  setLoadingType,
} from "../../data/errors";

interface Props {
  mainContainerRef: React.RefObject<HTMLDivElement>;
}

const RedeemScene = ({ mainContainerRef }: Props) => {
  const dispatch = useAppDispatch();
  const { l2Account } = useWalletContext();
  const nonce = useAppSelector(selectNonce);
  const redeemCostBase = useAppSelector(selectRedeemCostBase);
  const redeemRewardBase = useAppSelector(selectRedeemRewardBase);
  const redeemInfo = useAppSelector(selectRedeemInfo);
  const resources = useAppSelector(selectResources);
  const interest = useAppSelector(selectInterest);
  const bountyPool = useAppSelector(selectBountyPool);
  const isLoading = useAppSelector(selectIsLoading);
  const resourcesMap = Object.fromEntries(
    resources.map((resource: any) => [resource.type, resource.amount])
  );

  const containerRatio = 519 / 416;
  const containerSize = 0.6;
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [fontSize, setFontSize] = useState<number>(15);

  const adjustSize = () => {
    if (mainContainerRef.current) {
      const height = Math.min(
        mainContainerRef.current.offsetHeight,
        mainContainerRef.current.offsetWidth / containerRatio
      );
      setContainerHeight(height * containerSize);
      setContainerWidth(height * containerRatio * containerSize);
      setFontSize(height / 40);
    }
  };

  useEffect(() => {
    adjustSize();

    window.addEventListener("resize", adjustSize);
    return () => {
      window.removeEventListener("resize", adjustSize);
    };
  }, [mainContainerRef.current]);

  const onClickRedeem = (index: number) => {
    if (isLoading) {
      return;
    }

    dispatch(setLoadingType(LoadingType.Default));
    dispatch(
      sendTransaction({
        cmd: getRedeemTransactionCommandArray(nonce, index),
        prikey: l2Account!.getPrivateKey(),
      })
    ).then((action: any) => {
      if (sendTransaction.fulfilled.match(action)) {
        dispatch(queryState(l2Account.getPrivateKey())).then((action: any) => {
          if (queryState.fulfilled.match(action)) {
            dispatch(setUIState({ uIState: { type: UIStateType.Idle } }));
            dispatch(setLoadingType(LoadingType.None));
          }
        });
      } else if (sendTransaction.rejected.match(action)) {
        const message = "redeem Error: " + action.payload;
        dispatch(pushError(message));
        console.error(message);
        dispatch(setUIState({ uIState: { type: UIStateType.Idle } }));
        dispatch(setLoadingType(LoadingType.None));
      }
    });
  };

  const getRedeemCostAmount = (level: number): number => {
    return 20 * Math.pow(redeemCostBase, level);
  };

  const getRedeemRewardAmount = (level: number): number => {
    return redeemRewardBase * (level + 1);
  };

  const onClickCollectInterest = () => {
    dispatch(
      setUIState({ uIState: { type: UIStateType.CollectInterestPopup } })
    );
  };

  return (
    <div
      className="redeem-scene-container"
      style={{ width: containerWidth, height: containerHeight }}
    >
      <img src={background} className="redeem-scene-background" />
      <p className="redeem-scene-resource-title-text" style={{ fontSize }}>
        Redeem [available: {bountyPool ?? 0}]
      </p>
      <div className="redeem-scene-grid">
        <Grid
          columnCount={2}
          rowCount={4}
          elements={resourceTypes.map((type, index) =>
            type == ResourceType.Titanium ? (
              <RedeemDisplayCollectInterest
                key={index}
                isDisabled={interest <= 100}
                rewardIconImagePath={getResourceIconPath(ResourceType.Titanium)}
                rewardAmount={interest}
                onClickCollectInterest={onClickCollectInterest}
              />
            ) : (
              <RedeemDisplay
                key={index}
                isDisabled={
                  getRedeemCostAmount(redeemInfo[index]) > resourcesMap[type] ||
                  isLoading
                }
                costIconImagePath={
                  getRedeemCostAmount(redeemInfo[index]) > resourcesMap[type]
                    ? getResourceDisabledIconPath(type)
                    : getResourceIconPath(type)
                }
                costAmount={getRedeemCostAmount(redeemInfo[index])}
                rewardIconImagePath={getResourceIconPath(ResourceType.Titanium)}
                rewardAmount={getRedeemRewardAmount(redeemInfo[index])}
                onClickRedeem={() => onClickRedeem(index)}
              />
            )
          )}
        />
      </div>
    </div>
  );
};

export default RedeemScene;
